/**
 * 列表 / 比较相关的基础判定。
 *
 * ## 它们为什么在这儿（而不是在函数包里）
 *
 * 这些是**比较语义**，`docs/type-contract.md` §7 的 10 种操作符就是它。
 * 函数包里的 `list.filter` 节点只是**用**它 —— 通过 expr 的
 * `helpers.filterBy(...)` 间接调用。
 *
 * ⚠️ 走过一段弯路：目录收敛时 `compare` / `valueAt` 因为「内核里没人调用」
 * 被当成死代码删掉了。但加了数据包之后 `filterBy` 需要它们，
 * 于是又回来 —— 这次是**活代码**。
 * 当时的判断没错（没有消费者的实现就是死代码），
 * 教训是「删的时候要看清它是不是给未来的包预留的」。
 *
 * 现在文件里的分工：
 *   · `toNumber` / `isEmpty` —— 循环在用（`node/loop.ts`），expr 的 helper 也用；
 *   · `compare` / `valueAt` —— `helpers.filterBy` 在用；
 *   · `CompareOp` / `COMPARE_OPS` / `COMPARE_OP_LABELS` —— 契约词汇，
 *     既给 `filter` 节点的枚举控件用，也给 `verify:doc` 守文档。
 */
export type CompareOp =
  | 'eq'
  | 'ne'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'notContains'
  | 'empty'
  | 'notEmpty'

export const COMPARE_OPS: readonly CompareOp[] = [
  'eq',
  'ne',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'notContains',
  'empty',
  'notEmpty',
]

/** 操作符的中文名，给枚举控件用 */
export const COMPARE_OP_LABELS: Record<CompareOp, string> = {
  eq: '等于',
  ne: '不等于',
  gt: '大于',
  gte: '大于等于',
  lt: '小于',
  lte: '小于等于',
  contains: '包含',
  notContains: '不包含',
  empty: '为空',
  notEmpty: '不为空',
}

/** 能当数字读就返回数字，否则 null */
export function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'boolean') return value ? 1 : 0
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/** 什么算「空」—— 过滤节点和分支节点共用同一个定义，免得两处不一致 */
export function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true
  if (Array.isArray(value)) return value.length === 0
  return false
}



function looseEquals(a: unknown, b: unknown): boolean {
  const na = toNumber(a)
  const nb = toNumber(b)
  if (na !== null && nb !== null) return na === nb
  const sa = a === undefined || a === null ? '' : String(a)
  const sb = b === undefined || b === null ? '' : String(b)
  return sa === sb
}

function containsValue(actual: unknown, expected: unknown): boolean {
  if (Array.isArray(actual)) return actual.some((item) => looseEquals(item, expected))
  if (typeof actual === 'string') return actual.includes(String(expected ?? ''))
  return false
}



/* ══════════════════════════════════════════════════════════════
   分支选择
   ══════════════════════════════════════════════════════════════ */



/**
 * 一条比较。
 *
 * ⚠️ 语义的核心是「**能当数字读就按数字**」。数据源给的是 number，
 * 控件里手输的是 string，直接按字符串比的话 `"9" > "10"` 会判成真 ——
 * 而这种错**不报错**，只是输出的结果不对。
 *
 * | 操作符 | 语义 |
 * |---|---|
 * | `eq` / `ne` | 两边都能当数字 → 数字相等；否则严格相等 |
 * | `gt` `gte` `lt` `lte` | 同上，能当数字就按数字比，否则按字符串 |
 * | `contains` / `notContains` | 字符串按子串；数组按**元素**比（同样数字优先）；其它类型一律 false（不抛） |
 * | `empty` / `notEmpty` | 用 `isEmpty`（`0` 和 `false` **不算**空）|
 */
export function compare(actual: unknown, op: CompareOp, expected?: unknown): boolean {
  switch (op) {
    case 'eq':
      return looseEquals(actual, expected)
    case 'ne':
      return !looseEquals(actual, expected)
    case 'gt':
      return looseCompare(actual, expected) > 0
    case 'gte':
      return looseCompare(actual, expected) >= 0
    case 'lt':
      return looseCompare(actual, expected) < 0
    case 'lte':
      return looseCompare(actual, expected) <= 0
    case 'contains':
      return containsValue(actual, expected)
    case 'notContains':
      return !containsValue(actual, expected)
    case 'empty':
      return isEmpty(actual)
    case 'notEmpty':
      return !isEmpty(actual)
  }
}

/** 两边都能当数字就按数字比，否则按字符串。返回 -1 / 0 / 1 */
function looseCompare(a: unknown, b: unknown): number {
  const na = toNumber(a)
  const nb = toNumber(b)
  if (na !== null && nb !== null) return na === nb ? 0 : na < nb ? -1 : 1
  const sa = String(a ?? '')
  const sb = String(b ?? '')
  return sa === sb ? 0 : sa < sb ? -1 : 1
}

/**
 * 从一项里取字段（`filter` / `sort` 这类「按字段操作」用）。
 *
 * `field` 是**路径**（`'order.qty'`、`'items.0.name'`），空串表示「就是它自己」。
 * 中间任何一段缺失都返回 `undefined` —— 数据源少一个字段是常态。
 */
export function valueAt(item: unknown, field?: string): unknown {
  const path = (field ?? '').trim()
  if (path === '') return item
  let current: unknown = item
  for (const key of path.split('.')) {
    if (current === null || current === undefined) return undefined
    if (typeof current !== 'object' && !Array.isArray(current)) return undefined
    current = (current as Record<string, unknown>)[key.trim()]
  }
  return current
}
